import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { FintecObject, onErrorFunc } from '../types/tink.interface'
import { useLazyScript } from '../hooks/useLazyScript'
import { Loading } from './Loading'
import { ErrorCode } from '../constants/errorCodes'

type tinkScript = FintecObject | null

interface TinkLoaderProps {
  tink: tinkScript
  loading: boolean
  error: boolean
}
const TinkLoaderContext = createContext<TinkLoaderProps>({
  tink: null,
  loading: true,
  error: false,
})

export const TinkLoader: React.FC<{ onError: onErrorFunc }> = ({
  children,
  onError,
}) => {
  const { isLoading, hasError } = useLazyScript(process.env.SCRIPT_URL)

  const [tink, setTink] = useState<tinkScript>(null)

  useEffect(() => {
    if (hasError || isLoading) {
      return
    }

    setTink(window.xs2a)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, hasError])

  useEffect(() => {
    if (hasError) {
      onError(ErrorCode.SCRIPT_ERROR, ['Could not load tink script.'], false)
    }
  }, [hasError, onError])

  const ctx = useMemo(
    () => ({
      tink,
      loading: isLoading,
      error: hasError,
    }),
    [tink, isLoading, hasError],
  )

  if (isLoading) {
    return <Loading />
  }

  return (
    <TinkLoaderContext.Provider value={ctx}>
      {children}
    </TinkLoaderContext.Provider>
  )
}

export const useTinkLoader = () => useContext(TinkLoaderContext)
